import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { NAME_MAX, normalizeRoomCode } from '@shared/rules';
import { Scorebar, copyText, roomLink, useDocumentTitle } from '../../components/Chrome';
import { usePrefs } from '../../components/Prefs';
import { getName } from '../../lib/storage';
import { useRoom, type RoomApi } from '../../lib/useRoom';
import { GameOver } from './GameOver';
import { Lobby } from './Lobby';
import { TeamBar } from './Modes';
import { Play } from './Play';
import { RoomMenu } from './RoomMenu';

export function RoomPage() {
  const params = useParams();
  const code = normalizeRoomCode(params.code ?? '');
  const room = useRoom(code);
  useDocumentTitle(code ? `Room ${code}` : 'Room');

  if (room.status === 'connecting') {
    return (
      <main className="page center" style={{ paddingTop: '30vh' }}>
        <p className="muted">Connecting to room {code}...</p>
      </main>
    );
  }
  if (room.status === 'gone') return <Gone code={code} message={room.message} />;
  if (room.status === 'needs-name') return <JoinForm code={code} room={room} />;
  if (!room.view) {
    return (
      <main className="page center" style={{ paddingTop: '30vh' }}>
        <p className="muted">Loading the table...</p>
      </main>
    );
  }
  return <InRoom room={room} />;
}

function Gone({ code, message }: { code: string; message: string | null }) {
  return (
    <main className="page stack center" style={{ paddingTop: '18vh' }}>
      <img className="logo" src="/logo-mark.png" alt="" style={{ width: 110, margin: '0 auto' }} />
      <h1>{message?.includes('No room') ? `Room ${code} isn't here` : 'You left the room'}</h1>
      <p className="muted">{message ?? 'This room has ended.'}</p>
      <Link className="btn btn-primary btn-lg" to="/">
        Back to home
      </Link>
    </main>
  );
}

function JoinForm({ code, room }: { code: string; room: RoomApi }) {
  const [name, setName] = useState(getName());
  const [error, setError] = useState(room.message ?? '');
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return setError('What should we call you?');
    setBusy(true);
    const err = await room.join(name);
    setBusy(false);
    if (err) setError(err);
  }

  return (
    <main className="page" style={{ paddingTop: '10vh' }}>
      <form className="panel stack" onSubmit={submit}>
        <div className="center">
          <img className="logo" src="/logo-mark.png" alt="" style={{ width: 96 }} />
          <h1>Join room {code}</h1>
          <p className="muted">Pick a name everyone will recognise.</p>
        </div>
        <input
          className="input"
          autoFocus
          value={name}
          maxLength={NAME_MAX}
          onChange={(e) => setName(e.target.value)}
          placeholder="Your name"
          aria-label="Your name"
          autoComplete="nickname"
        />
        {error && <p className="error" role="alert">{error}</p>}
        <button className="btn btn-primary btn-lg" disabled={busy}>
          {busy ? 'Joining...' : 'Take a seat'}
        </button>
        <Link to="/" className="center small">
          Back to home
        </Link>
      </form>
    </main>
  );
}

function InRoom({ room }: { room: RoomApi }) {
  const view = room.view!;
  const { toast } = usePrefs();
  const navigate = useNavigate();
  const [menu, setMenu] = useState(false);
  const inGame = view.phase !== 'lobby';

  async function copyCode() {
    if (await copyText(roomLink(view.code))) toast('Invite link copied.');
  }

  return (
    <div className="game">
      {!room.online && <div className="offline-banner">Reconnecting...</div>}
      <header className="game-header">
        <div className="inner">
          <div className="row between" style={{ flexWrap: 'nowrap' }}>
            <Link to="/" aria-label="Home" className="row" style={{ textDecoration: 'none', flexWrap: 'nowrap' }}>
              <img className="logo" src="/logo-mark.png" alt="" style={{ width: 32, height: 32 }} />
            </Link>
            <button type="button" className="code" onClick={copyCode} aria-label={`Room code ${view.code}, tap to copy the invite link`}>
              {view.code}
            </button>
            <span className="grow" />
            {inGame && view.round && <span className="chip blue">Round {view.round.number}</span>}
            <button type="button" className="icon-btn" aria-label="Menu" onClick={() => setMenu(true)}>
              ☰
            </button>
          </div>
          {inGame && <Scorebar players={view.players} meId={view.me?.id} />}
          {inGame && <TeamBar view={view} meId={view.me?.id} />}
        </div>
      </header>

      {view.phase === 'lobby' && <Lobby room={room} />}
      {view.phase !== 'lobby' && view.phase !== 'game_over' && <Play room={room} />}
      {view.phase === 'game_over' && <GameOver room={room} />}

      {menu && (
        <RoomMenu
          room={room}
          onClose={() => setMenu(false)}
          onLeft={() => {
            setMenu(false);
            navigate('/');
          }}
        />
      )}
    </div>
  );
}
